import { AclVerb } from '@agilelab/plugin-wb-access-control-common';
import {
  ComponentInstanceEntity,
  ConsumableMode,
  InstanceEntity,
  parseConsumableInterfacesUrns,
  toConsumableInterface,
} from '@agilelab/plugin-wb-marketplace-common';
import {
  UserEntity,
  WbButton,
  WbDrawer,
  WbDrawerContent,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import {
  alertApiRef,
  AnalyticsContext,
  configApiRef,
  identityApiRef,
  useApi,
  useElementFilter,
} from '@mainstage/core-plugin-api';
import {
  Box,
  createStyles,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { IChangeEvent } from '@rjsf/core';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AccessControlRequestTemplate } from '@agilelab/plugin-wb-builder-common';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';
import {
  LayoutOptions,
  LAYOUTS_KEY,
  LAYOUTS_WRAPPER_KEY,
  MultiStepJsonForm,
  scaffolderApiRef,
  useFormSteps,
  useTemplateParameterSchema,
} from '@agilelab/plugin-wb-scaffolder';
import { Entity, stringifyEntityRef } from '@mainstage/catalog-model';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { useOutlet } from 'react-router';
import useAsync from 'react-use/lib/useAsync';
import { useProductPendingRequestsLink } from '@agilelab/plugin-wb-access-control';

type AccessControlRequestDialogProps = {
  isOpen: boolean;
  template: AccessControlRequestTemplate | undefined;
  systemInstance: InstanceEntity;
  selectedOutputPorts: ComponentInstanceEntity[];
  userEntity: UserEntity;
  onRequestSent: () => void;
  setIsOpen: (value: boolean) => void;
  setScaffolderTaskIds: (taskIds: string[]) => void;
  verb: AclVerb;
  /**
   * Indicates the resource on which the request was triggered
   */
  requestSource: 'system' | 'component';
  /**
   * When provided, form submission calls onNext(formState) instead of directly sending requests.
   * Used by the multi-template dialog to collect form data before firing all requests at once.
   */
  onNext?: (formState: Record<string, any>) => void;
  /** Label for the submit button. Defaults to "Send". */
  submitLabel?: string;
  /**
   * Optional slot rendered to the left of the form, used by MultiTemplateAccessRequestDialog
   * to show the technology group navigation list.
   */
  groupListSlot?: React.ReactNode;
  /**
   * When provided, a Back button is shown and this callback is invoked on click.
   * Used by MultiTemplateAccessRequestDialog to navigate to the previous group.
   */
  onBack?: () => void;
  /**
   * Initial form data to pre-populate the form. Used by MultiTemplateAccessRequestDialog
   * to restore previously filled values when navigating back to a group.
   */
  initialFormData?: Record<string, any>;
};

type ResourceItem = {
  id: number;
  name: string;
  /**
   * Whether the access request is forwarded to the resource's children
   */
  forwardsToChildren: boolean;
};

const createContext = (
  systemInstance: InstanceEntity,
  outputPort: ComponentInstanceEntity,
  userEntity: UserEntity,
  requesterRawEntity: Entity | undefined,
  consumableInterfaceTypeField: string,
  requestsManagementUrl: string,
) => {
  const parsedConsumableInterface = toConsumableInterface(outputPort);
  return {
    requestsManagementUrl,
    id_output_port: outputPort?.id,
    id_dataproduct_instance: systemInstance.id,
    requester: userEntity.username,
    requester_display_name: userEntity.displayName,
    environment: systemInstance.environment.name,
    dataproduct: {
      id: systemInstance.id,
      name: systemInstance.name,
      domain: systemInstance.domains[0].data.name,
      version: systemInstance.version,
      display_name: systemInstance.display_name,
      data_product_owner: systemInstance.owner,
      data_product_display_name: systemInstance.owner_display_name,
      external_id: systemInstance.external_id,
    },
    outputport: {
      name: outputPort.name,
      output_port_type:
        outputPort.descriptor[consumableInterfaceTypeField] ?? 'unavailable',
      external_id: outputPort.external_id,
      consumableInterfaces: parsedConsumableInterface
        ? parseConsumableInterfacesUrns(parsedConsumableInterface)
        : [],
    },
    recipient: systemInstance.owner,
    sender: userEntity.username,
    requesterRawEntity: requesterRawEntity,
  };
};

export const cloneFormWithDescriptor = (
  formData: Record<string, any>,
  selectedOutputPort: ComponentInstanceEntity,
  dataProductInstance: InstanceEntity,
  userEntity: UserEntity,
  requesterRawEntity: Entity | undefined,
  consumableInterfaceTypeField: string,
  requestsManagementUrl: string,
) => {
  return {
    ...formData,
    __consumableInterface__: selectedOutputPort.descriptor,
    __context__: createContext(
      dataProductInstance,
      selectedOutputPort,
      userEntity,
      requesterRawEntity,
      consumableInterfaceTypeField,
      requestsManagementUrl,
    ),
  };
};

/**
 * Checks whether the access request is forwarded to the resource's children
 * @TODO update this logic when after future developments access requests could be directly triggered on subcomponents. Target (future) behavior: return true only if parent component is shoppable
 */
const forwardsToChildren = (component: ComponentInstanceEntity) =>
  component.components?.some(
    c => c.data.consumable === ConsumableMode.Consumable,
  ) ?? false;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formRow: {
      marginBottom: theme.spacing(2),
    },
  }),
);

export const AccessControlRequestDialog = (
  props: AccessControlRequestDialogProps,
) => {
  const {
    template,
    selectedOutputPorts,
    systemInstance,
    userEntity,
    requestSource,
    setIsOpen,
    onRequestSent,
    setScaffolderTaskIds,
    onNext,
    submitLabel = 'Send',
    groupListSlot,
    onBack,
    initialFormData,
  } = props;
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const { value: requesterRawEntity } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();
    return catalogApi.getEntityByRef(expandEntityRef(userEntity.username), {
      token,
    });
  }, [catalogApi]);

  const productPendingRequestsLink = useProductPendingRequestsLink(
    systemInstance.id,
  );

  const consumableInterfaceTypeField = configApi.getString(
    'practiceShaper.migration.consumableInterfaceTypeField',
  );
  const maxNumberOfVisibleResources = configApi.getOptionalNumber(
    'mesh.marketplace.ui.accessControl.requestDialog.maxNumberOfVisibleResources',
  );

  const [formState, setFormState] = useState<Record<string, any>>(() => {
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      return initialFormData;
    }
    return {
      __consumableInterface__:
        selectedOutputPorts && selectedOutputPorts[0]
          ? selectedOutputPorts[0].descriptor
          : undefined,
      __context__: createContext(
        systemInstance,
        selectedOutputPorts[0],
        userEntity,
        requesterRawEntity,
        consumableInterfaceTypeField,
        productPendingRequestsLink,
      ),
    };
  });

  useEffect(() => {
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      return;
    }
    setFormState({
      __consumableInterface__:
        selectedOutputPorts && selectedOutputPorts[0]
          ? selectedOutputPorts[0].descriptor
          : undefined,
      __context__: createContext(
        systemInstance,
        selectedOutputPorts[0],
        userEntity,
        requesterRawEntity,
        consumableInterfaceTypeField,
        productPendingRequestsLink,
      ),
    });
  }, [
    systemInstance,
    selectedOutputPorts,
    userEntity,
    props.isOpen,
    requesterRawEntity,
    consumableInterfaceTypeField,
    initialFormData,
    productPendingRequestsLink,
  ]);

  const scaffolderApi = useApi(scaffolderApiRef);
  const templateRef = template
    ? stringifyEntityRef({
        name: template.metadata.name,
        kind: template.kind,
        namespace: template.metadata.namespace ?? 'default',
      })
    : '';
  const classes = useStyles();
  const { schema, loading: schemaLoading } =
    useTemplateParameterSchema(templateRef);

  const outlet = useOutlet();

  const customLayouts = useElementFilter(outlet, elements =>
    elements
      .selectByComponentData({
        key: LAYOUTS_WRAPPER_KEY,
      })
      .findComponentData<LayoutOptions>({
        key: LAYOUTS_KEY,
      }),
  );

  const { steps: processedSteps, loading: stepsLoading } = useFormSteps(
    schema?.steps,
    {
      layouts: customLayouts,
    },
  );

  const loading = schemaLoading || stepsLoading;

  const handleChange = useCallback(
    (e: IChangeEvent) => {
      setFormState(e.formData);
    },
    [setFormState],
  );

  const handleSendRequest = useCallback(async () => {
    if (onNext) {
      onNext(formState);
      setFormState({});
      return;
    }
    try {
      const scaffolderResponses = await Promise.all(
        selectedOutputPorts.map(async outputPort => {
          return scaffolderApi.scaffold({
            templateRef,
            values: cloneFormWithDescriptor(
              formState,
              outputPort,
              systemInstance,
              userEntity,
              requesterRawEntity,
              consumableInterfaceTypeField,
              productPendingRequestsLink,
            ),
          });
        }),
      );
      onRequestSent();
      const taskIds = scaffolderResponses.map(response => response.taskId);
      setScaffolderTaskIds(taskIds);
    } catch (error) {
      alertApi.post({
        message: error.message,
        severity: 'error',
      });
    }
    setFormState({});
    setIsOpen(false);
  }, [
    onNext,
    setIsOpen,
    scaffolderApi,
    templateRef,
    formState,
    selectedOutputPorts,
    onRequestSent,
    setScaffolderTaskIds,
    alertApi,
    systemInstance,
    requesterRawEntity,
    userEntity,
    consumableInterfaceTypeField,
    productPendingRequestsLink,
  ]);

  const { visibleResources, remainingResourcesCount } = useMemo(() => {
    let selectedResources: ResourceItem[] = [];

    if (requestSource === 'system') {
      selectedResources = [
        {
          id: systemInstance.id,
          name: systemInstance.name,
          forwardsToChildren: true,
        },
      ];
    } else if (requestSource === 'component') {
      selectedResources = selectedOutputPorts.map(outputPort => ({
        id: outputPort.id,
        name: outputPort.name,
        forwardsToChildren: forwardsToChildren(outputPort),
      }));
    }

    const visibleRes =
      maxNumberOfVisibleResources && maxNumberOfVisibleResources > 0
        ? selectedResources.slice(0, maxNumberOfVisibleResources)
        : selectedResources;

    return {
      visibleResources: visibleRes,
      remainingResourcesCount: selectedResources.length - visibleRes.length,
    };
  }, [
    maxNumberOfVisibleResources,
    requestSource,
    selectedOutputPorts,
    systemInstance.id,
    systemInstance.name,
  ]);

  return (
    <WbDrawer
      open={props.isOpen}
      setOpen={setIsOpen}
      title={`Access Control - ${
        props.verb === AclVerb.Grant ? 'Request access' : 'Request a revoke'
      }`}
      size={groupListSlot ? 75 : 50}
      actions={
        <>
          <WbButton
            color="primary"
            variant="outlined"
            label="Cancel"
            onClick={() => setIsOpen(false)}
          />
          {onBack && (
            <WbButton
              color="default"
              variant="outlined"
              label="Back"
              onClick={onBack}
            />
          )}
          <WbButton
            color="primary"
            type="submit"
            variant="contained"
            label={submitLabel}
            form="access-request-form"
          />
        </>
      }
    >
      <WbDrawerContent style={{ overflowX: 'hidden' }}>
        {loading && <LinearProgress />}
        {schema && processedSteps && !loading && (
          <>
            <Box style={{ display: 'flex', minHeight: '100%' }}>
              {groupListSlot}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography>
                  {props.verb === AclVerb.Grant
                    ? 'You are requesting access to: '
                    : 'You are requesting a revoke for: '}
                </Typography>
                {visibleResources.length > 0 && (
                  <List>
                    {visibleResources.map(resource => (
                      <ListItem key={resource.id} style={{ padding: '0' }}>
                        <ListItemText
                          primary={
                            <WbTooltip
                              title={
                                resource.forwardsToChildren
                                  ? '* the request extends to all its children'
                                  : ''
                              }
                            >
                              <Typography color="primary" component="span">
                                {resource.name}
                                {resource.forwardsToChildren && ' *'}
                              </Typography>
                            </WbTooltip>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {remainingResourcesCount > 0 && (
                  <Typography>
                    and <strong>{remainingResourcesCount}</strong> more resource
                    {remainingResourcesCount > 1 ? 's' : ''}
                  </Typography>
                )}
                <Box className={classes.formRow}>
                  <AnalyticsContext attributes={{ entityRef: templateRef }}>
                    {processedSteps.length > 0 ? (
                      <MultiStepJsonForm
                        rootStepperStyle={{ padding: '0' }}
                        stepContentStyle={{ padding: '0', margin: '0' }}
                        formData={formState}
                        customOnSubmitFn={handleSendRequest}
                        customBackButton={<></>}
                        customNextButton={<></>}
                        onChange={handleChange}
                        viewStepperLabel={false}
                        steps={processedSteps}
                        formId="access-request-form"
                      />
                    ) : (
                      <form
                        id="access-request-form"
                        onSubmit={e => {
                          e.preventDefault();
                          handleSendRequest();
                        }}
                      />
                    )}
                  </AnalyticsContext>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </WbDrawerContent>
    </WbDrawer>
  );
};
